// src/component/PromoBannerDialog.js
//
// サイト上部プッシュ通知の「初回〜日次1回」用ダイアログ。
// previewMode=true のときは本物のモーダル Dialog を使わず、
// インラインカードとして同じ見た目を描画する（管理画面プレビュー用）。

import React, { useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
} from "@mui/material";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { AppThemeContext } from "./AppShell";

export default function PromoBannerDialog({
  open,
  chipLabel,
  title,
  body,
  imageUrl,
  linkUrl,
  linkLabel,
  onCloseToday,
  onDismissForever,
  previewMode = false,
}) {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const textBase = theme.railText || "rgba(30,30,60,0.92)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const handleLinkClick = () => {
    if (!linkUrl) return;
    window.open(linkUrl, "_blank", "noopener,noreferrer");
  };

  const titleNode = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <CampaignRoundedIcon sx={{ fontSize: 22, color: accent, mt: "-1px" }} />
      <Box>
        <Typography
          variant="overline"
          sx={{
            display: "block",
            letterSpacing: "0.26em",
            fontSize: 11,
            opacity: 0.8,
          }}
        >
          {chipLabel || "お知らせ"}
        </Typography>
        <Typography
          component="div"
          sx={{
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: "0.04em",
          }}
        >
          {title}
        </Typography>
      </Box>
    </Stack>
  );

  const bodyNode = (
    <>
      {imageUrl ? (
        <Box
          sx={{
            mb: 2,
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${border}`,
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt=""
            sx={{
              display: "block",
              width: "100%",
              maxHeight: 320,
              objectFit: "cover",
            }}
          />
        </Box>
      ) : null}

      {body ? (
        <Box
          sx={{
            maxHeight: 160,
            overflowY: "auto",
            pr: 1,
            mr: -1,
            scrollbarGutter: "stable",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.85,
              color: textBase,
            }}
          >
            {body}
          </Typography>
        </Box>
      ) : null}

      {linkUrl ? (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Button
            onClick={handleLinkClick}
            variant="contained"
            endIcon={<OpenInNewRoundedIcon />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
            }}
          >
            {linkLabel || "詳しく見る"}
          </Button>
        </>
      ) : null}
    </>
  );

  const actionsNode = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        borderTop: `1px solid ${border}`,
        bgcolor: "rgba(255,255,255,0.96)",
        px: 2.5,
        py: 1.5,
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Button
        onClick={onDismissForever}
        sx={{
          textTransform: "none",
          color: "rgba(0,0,0,0.55)",
          fontWeight: 600,
        }}
      >
        今後表示しない
      </Button>
      <Box sx={{ flex: 1 }} />
      <Button
        onClick={onCloseToday}
        variant="outlined"
        sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
      >
        閉じる
      </Button>
    </Stack>
  );

  // -------------------- プレビュー（インライン） --------------------
  if (previewMode) {
    return (
      <Box
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${border}`,
          boxShadow: "0 22px 60px rgba(0,0,0,0.18)",
          bgcolor: surface,
          maxWidth: 600,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            color: textBase,
            px: 3,
            pt: 2,
            pb: 1,
            borderBottom: `1px solid ${border}`,
            bgcolor: `${accent}07`,
          }}
        >
          {titleNode}
        </Box>
        <Box sx={{ p: 2 }}>{bodyNode}</Box>
        {actionsNode}
      </Box>
    );
  }

  // -------------------- 通常表示（モーダル Dialog） --------------------
  return (
    <Dialog
      open={open}
      onClose={onCloseToday}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${border}`,
          boxShadow: "0 22px 60px rgba(0,0,0,0.18)",
          bgcolor: surface,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: textBase,
          pb: 1,
          borderBottom: `1px solid ${border}`,
          bgcolor: `${accent}07`,
        }}
      >
        {titleNode}
      </DialogTitle>

      <DialogContent dividers>{bodyNode}</DialogContent>

      <DialogActions sx={{ p: 0 }}>{actionsNode}</DialogActions>
    </Dialog>
  );
}
